<script lang="ts">
	import { Section, SectionHeader, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import type { PortfolioAsset } from "#backend/modules/portfolio/portfolio.schemas.js";
	import { capitalStructureColors } from "./portfolio-map/portfolio-map.types";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";

	interface Props {
		id?: string;
		label?: string;
		title?: string;
		subtitle?: string;
		investments: PortfolioAsset[];
		theme?: SectionTheme;
		class?: string;
	}

	let {
		id,
		label,
		title,
		subtitle,
		investments,
		theme = "dark",
		class: className
	}: Props = $props();

	function buildDetails(asset: PortfolioAsset) {
		const details: { label: string; value: string }[] = [];

		if (asset.purchaseDate) {
			details.push({
				label: "Acquired",
				value: new Date(asset.purchaseDate).getFullYear().toString()
			});
		} else if (asset.yearBuilt) {
			details.push({ label: "Built", value: asset.yearBuilt.toString() });
		}

		const typeLabel =
			asset.assetType === "Multi-Family" ? "Apartment Complex" : asset.assetType;
		details.push({ label: "Type", value: typeLabel });

		if (asset.unitCount && asset.grossLeasableArea) {
			const unitWord = asset.assetType === "Commercial" ? "Suites" : "Units";
			details.push({
				label: "Size",
				value: `${asset.unitCount} ${unitWord} at ${asset.grossLeasableArea.toLocaleString()} SF`
			});
		}

		details.push({ label: "Status", value: "Funded & Under Management" });
		return details;
	}
</script>

<Section {id} {theme} class={className}>
	<SectionHeader {label} {title} {subtitle} />

	<div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
		{#each investments as investment (investment.id)}
			{@const details = buildDetails(investment)}
			{@const structureColor = capitalStructureColors[investment.capitalStructure]?.hex}
			<div
				class="bg-card text-card-foreground group flex h-full flex-col overflow-hidden rounded border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
			>
				{#if investment.imageUrl}
					<div class="relative h-52 shrink-0 overflow-hidden">
						<img
							src={cloudinaryFetch(investment.imageUrl, { width: 400 })}
							alt={investment.displayName}
							class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
							width="400"
							height="208"
							loading="lazy"
						/>
						<div
							class="absolute inset-0 bg-linear-to-t from-black/40 to-transparent"
						></div>
						<div class="absolute bottom-0 left-0 flex items-center gap-2 p-4">
							<span
								class="bg-primary inline-block rounded-full px-3 py-1 text-xs font-semibold text-white"
							>
								{investment.city}, {investment.state}
							</span>
							{#if structureColor}
								<span
									class="inline-block rounded-full px-3 py-1 text-xs font-semibold text-white"
									style="background-color: {structureColor};"
								>
									{investment.capitalStructure}
								</span>
							{/if}
						</div>
					</div>
				{/if}

				<div class="flex grow flex-col p-5">
					<p class="text-card-foreground mb-3 text-xl font-bold">
						{investment.displayName}
					</p>

					<div class="mt-auto border-t pt-4">
						<div class="space-y-2">
							{#each details as detail (detail.label)}
								<div class="flex items-center text-sm">
									<span class="text-muted-foreground font-medium"
										>{detail.label}:</span
									>
									<span class="text-card-foreground ml-2">{detail.value}</span>
								</div>
							{/each}
						</div>
					</div>
				</div>
			</div>
		{/each}
	</div>
</Section>
